import React, { useEffect } from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';

import FieldArrayIterables from '~/commons/components/FieldArrayIterables.jsx';
import FormGroup from '~/commons/components/FormGroup.jsx';
import FormField from '~/commons/components/FormField.jsx';
import { numToLetter } from '~/commons/helpers';

const FieldsMatching = ({
  qtnIndex: i,
  restrictEdit = false,
  fileUploadUrl,
}) => {
  const [, { value }] = useField(`questions.${i}`);
  const [,, { setValue: setAns }] = useField(`questions.${i}.answers`);
  const [,, { setValue: setMatches }] = useField(`questions.${i}.matches`);
  const { t } = useTranslation();
  let { choices } = value;
  const { answers, matches } = value;
  // take out the last empty choice, but ensure structure is always {text, img}
  if (!restrictEdit && !choices.slice(-1)[0].text && !choices.slice(-1)[0].img) {
    choices = choices.slice(0, -1);
  }

  // Ensure all choices and matches are objects with text/img keys
  const safeMatches = (matches || []).map((m) => m);
  useEffect(() => {
    if (
      answers.length !== choices.length
      || matches.length !== choices.length
    ) {
      // ensure right number of answers and matches
      let a = answers.slice(0, choices.length);
      const m = matches.slice(0, choices.length);

      a.push(...choices.slice(a.length).map(() => null));
      m.push(...choices.slice(m.length).map(() => ''));
      // make sure answers are within one of the choices
      a = a.map((k, ii) => (
        k === null || k >= choices.length
          ? ii.toString() : k.toString()
      ));

      setAns(a);
      setMatches(m);
    }
  }, [choices.length]);

  return (
    <div className="row">
      <div className="col">
        <label>{t('Clues')} *</label>
        <FieldArrayIterables
          name={`questions.${i}.choices`}
          {...restrictEdit ? {} : {
            tail: { text: '', img: '' }, // Always use object structure for new clues
          }}
        >
          {(c, ii) => (
            <div className="form-row align-items-start mb-3" key={ii}>
              <div className="col-auto pt-2">
                {(ii + 1)}.
              </div>
              <div className="col">
                {/* Clue Text Input */}
                <FormGroup
                  name={`questions.${i}.choices.${ii}.text`}
                  label="Clue Text"
                  placeholder="Enter clue text..."
                />

                {/* Image Upload */}
                <FormGroup as="coverImg" api={fileUploadUrl} name={`questions.${i}.choices.${ii}.img`} label="Clue Image (optional)" className="mt-2" />
              </div>
            </div>
          )}
        </FieldArrayIterables>
      </div>
      <div className="col">
        <label>{t('Matches')} *</label>
        {safeMatches.map((m, ii) => (
          <div key={ii} className="mb-3">
            <div className="d-flex align-items-start">
              <FormField
                name={`questions.${i}.answers.${ii}`}
                as="select"
                className="me-2"
                style={{ width: '80px' }}
              >
                {choices.map((c, iii) => (
                  <option
                    key={iii}
                    value={iii}
                  >{(iii + 1)}</option>
                ))}
              </FormField>
              <div className="d-flex align-items-center p-2">{numToLetter(ii, true)}.</div>
              <div className="flex-grow-1">
                {/* Match Text Input */}
                <FormGroup
                  name={`questions.${i}.matches.${ii}.text`}
                  label="Match Text"
                  placeholder="Enter match text..."
                />

                {/* Match Image Upload */}
                <FormGroup as="coverImg" api={fileUploadUrl} name={`questions.${i}.matches.${ii}.img`} label="Match Image (optional)" className="mt-2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FieldsMatching;
